<script setup lang="ts">

import { computed } from 'vue';
import { usePresentation } from '@/modules/Presentation/Types';
import { useLead } from "../../Types/Lead";
import { Map } from '../../Types/Map';
import { AgeGroup, Gender, Interest, LeadType } from '../../Types/LeadOptions';

const lead = useLead();
const presentation = usePresentation();

presentation.backgroundImage = '/images/sample/gkr-watermark.png';
presentation.onSetNext = () => ({ section: Map.YourDojo, page: 0 });
//presentation.onSetNext = () => ({ section: lead.hasFemale && lead.ageGroup == AgeGroup.band4 ? Map.ForWomen : Map.YourDojo, page: 0 });

const imageSrc = computed( () => 
{

    if( lead.gender == Gender.male && lead.ageGroup == AgeGroup.band4 )
    {
        return 'male-adult';
    }

    if( lead.gender == Gender.female && lead.ageGroup == AgeGroup.band4 )
    {
        return 'female-adult';
    }

    if( lead.interest == Interest.confidence || lead.interest == Interest.selfDefence )
    {
        return lead.hasChild ? 'bullying' : 'confidence'
    }

    if( lead.interest == Interest.selfDiscipline )
    {
        return 'self-discipline'
    }

    if( lead.interest == Interest.competition || lead.interest == Interest.fitness )
    {
        return 'competition'
    }

    return 'confidence';
    
})

const video = computed( () => 
{

    if( lead.hasTeenager )
    {
        return { 
            title: "Self-defence empowers people of all ages",
            previewSrc: '/YourGoal/why-join-gkr-teenager-preview.jpg',
            src: '~/video/gkr/why-join-gkr-teenager.mp4'
        }
    }

    if( lead.hasChild )
    {
        return { 
            title: "Self-defence empowers people of all ages",
            previewSrc: '/YourGoal/why-join-gkr-child-preview.jpg',
            src: '~/video/gkr/why-join-gkr-child.mp4'
        }
    }

    return { 
        title: "Self-defence empowers people of all ages",
        previewSrc: '/YourGoal/why-join-gkr-adult-preview.jpg',
        src: '~/video/gkr/why-join-gkr-adult.mp4'
    }

});

</script>

<template>
    <Page>

        <div class="grid grid-cols-2 gap-6">
            <Image :src="`/YourGoal/${imageSrc}.jpg`" class="rounded-lg w-full" shadow />
            <button class="bg-black rounded-lg flex items-center p-4"
                @click="() => presentation.displayMedia( video.src! )">
                <Image :src="video.previewSrc" class="rounded-lg w-full" shadow />
            </button>
            <div class="col-span-2 text-center">

                <!-- BULLYING -->
                <div v-if="lead.hasChild && ( lead.interest == Interest.confidence || lead.interest == Interest.selfDefence )">
                    <h1 class="text-2xl font-semibold mb-4">Build Confidence / Overcome Bullying</h1>
                    <p class="text-lg">
                        If your child is facing bullying—or just needs help standing tall — karate builds 
                        the resilience, confidence, and self-awareness to change how they carry themselves. 
                        With expert instructors and a supportive community, we teach more than defence — 
                        we teach courage.
                    </p>
                </div>

                <!-- CONFIDENCE / SELF DEFENCE -->
                <div v-else-if="lead.interest == Interest.confidence || lead.interest == Interest.selfDefence">
                    <h1 class="text-2xl font-semibold mb-4">Build Confidence &amp; Learn Self-Defence</h1>
                    <p class="text-lg">
                        Whether it’s feeling more secure walking to your car at night or standing your 
                        ground in everyday situations, our programs are designed to help you grow stronger, 
                        more confident, and more in control—with expert instructors by your side 
                        every step of the way.
                    </p>
                </div>

                <!-- COMPETITION -->
                <div v-else-if="lead.interest == Interest.competition">
                    <h1 class="text-2xl font-semibold mb-4">Embrace Healthy Competition</h1>
                    <p class="text-lg">
                        If you're driven by goals, progress, and a little friendly rivalry, you'll love 
                        our tournament pathway and belt system. Karate offers a positive outlet to test 
                        your skills, challenge yourself, and grow—on the mats and in life—with the full 
                        support of a like-minded team.
                    </p>
                </div>

                <!-- FITNESS -->
                <div v-else-if="lead.interest == Interest.fitness">
                    <h1 class="text-2xl font-semibold mb-4">Improve Fitness &amp; Wellbeing</h1>
                    <p class="text-lg">
                        Whether you want to feel stronger, move better, or have more energy to keep up 
                        with your kids, our karate programs deliver full-body fitness in a fun, structured 
                        way. Build flexibility, endurance, and strength while training alongside others 
                        who support your goals.
                    </p>
                </div>

                <!-- SELF-DISCIPLINE -->
                <div v-else="lead.interest == Interest.selfDiscipline">
                    <h1 class="text-2xl font-semibold mb-4">Develop Discipline &amp; Focus</h1>
                    <p class="text-lg">
                        Whether it’s for yourself or your child, our classes are built on structure, 
                        respect, and goal-setting. Karate helps students learn to follow instructions, 
                        focus under pressure, and stay committed—all while building real-world 
                        confidence through gradual progress.
                    </p>
                </div>
            </div>
        </div>

    </Page>

</template>